import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import { supabase, isSupabaseConfigured } from './supabaseClient';
import {
  getMarketplaceData,
  createListing,
  createOrder,
  createStripeConnectLink,
  deleteListing,
  getAccountProfile,
  getOrderDetail,
  sendOrderMessage,
  submitOrderReview,
  updateOrderStatus,
  uploadOrderFile,
  updateAccountProfile,
  updateListing,
} from './lib/marketplace';
import { MarketplaceLayout } from './components/MarketplaceLayout';
import { BuilderInviteModal } from './components/BuilderInviteModal';
import { HomePage } from './pages/HomePage';
import { ExplorePage } from './pages/ExplorePage';
import { ListingPage } from './pages/ListingPage';
import { StudioPage } from './pages/StudioPage';
import { LoginPage } from './pages/LoginPage';
import { AccountPage } from './pages/AccountPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { OrderPage } from './pages/OrderPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { MessagesPage } from './pages/MessagesPage';

function App() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(Boolean(isSupabaseConfigured && supabase));
  const currentUser = useMemo(() => session?.user ?? null, [session]);
  const currentUserId = currentUser?.id ?? null;

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      const nextSnapshot = await getMarketplaceData(currentUserId ? { id: currentUserId } : null);

      if (mounted) {
        setSnapshot(nextSnapshot);
        setLoading(false);
      }
    }

    setLoading(true);
    loadData();

    return () => {
      mounted = false;
    };
  }, [currentUserId]);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      setAuthLoading(false);
      return undefined;
    }

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setAuthLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  async function handleListingCreate(payload) {
    const result = await createListing(payload, currentUser?.id);
    const nextSnapshot = await getMarketplaceData(currentUser);
    setSnapshot(nextSnapshot);
    return result;
  }

  async function handleListingUpdate(listingId, payload) {
    const result = await updateListing(listingId, payload, currentUser?.id);
    const nextSnapshot = await getMarketplaceData(currentUser);
    setSnapshot(nextSnapshot);
    return result;
  }

  async function handleOrderCreate(payload) {
    const result = await createOrder(payload, currentUser);
    if (!result.checkoutUrl) {
      const nextSnapshot = await getMarketplaceData(currentUser);
      setSnapshot(nextSnapshot);
    }
    return result;
  }

  async function handleProfileLoad(user) {
    return getAccountProfile(user);
  }

  async function handleProfileUpdate(payload) {
    const result = await updateAccountProfile(currentUser?.id, payload, currentUser);
    const nextSnapshot = await getMarketplaceData(currentUser);
    setSnapshot(nextSnapshot);
    return result;
  }

  async function handleListingDelete(listingId) {
    const result = await deleteListing(listingId);
    const nextSnapshot = await getMarketplaceData(currentUser);
    setSnapshot(nextSnapshot);
    return result;
  }

  async function handleOrderLoad(orderId) {
    return getOrderDetail(orderId, currentUser);
  }

  async function handleOrderMessage(orderId, body) {
    const result = await sendOrderMessage(orderId, currentUser, body);
    const nextSnapshot = await getMarketplaceData(currentUser);
    setSnapshot(nextSnapshot);
    return result;
  }

  async function handleOrderFileUpload(orderId, file, fileKind) {
    const result = await uploadOrderFile(orderId, currentUser, file, fileKind);
    const nextSnapshot = await getMarketplaceData(currentUser);
    setSnapshot(nextSnapshot);
    return result;
  }

  async function handleOrderStatusUpdate(orderId, nextStatus) {
    const result = await updateOrderStatus(orderId, currentUser, nextStatus);
    const nextSnapshot = await getMarketplaceData(currentUser);
    setSnapshot(nextSnapshot);
    return result;
  }

  async function handleOrderReview(orderId, payload) {
    const result = await submitOrderReview(orderId, currentUser, payload);
    const nextSnapshot = await getMarketplaceData(currentUser);
    setSnapshot(nextSnapshot);
    return result;
  }

  async function handleStripeConnectLink(returnPath) {
    return createStripeConnectLink(currentUser, returnPath);
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <MarketplaceLayout snapshot={snapshot} loading={loading} currentUser={currentUser}>
        <Routes>
          <Route path="/" element={<HomePage snapshot={snapshot} loading={loading} />} />
          <Route path="/explore" element={<ExplorePage snapshot={snapshot} loading={loading} />} />
          <Route
            path="/agents/:slug"
            element={
              <ListingPage
                snapshot={snapshot}
                loading={loading}
                currentUser={currentUser}
                onCreateOrder={handleOrderCreate}
              />
            }
          />
          <Route
            path="/studio"
            element={
              <StudioPage
                snapshot={snapshot}
                loading={loading}
                authLoading={authLoading}
                currentUser={currentUser}
                onLoadProfile={handleProfileLoad}
                onCreateListing={handleListingCreate}
                onUpdateListing={handleListingUpdate}
                onCreateStripeConnectLink={handleStripeConnectLink}
              />
            }
          />
          <Route
            path="/studio/edit/:listingId"
            element={
              <StudioPage
                snapshot={snapshot}
                loading={loading}
                authLoading={authLoading}
                currentUser={currentUser}
                onLoadProfile={handleProfileLoad}
                onCreateListing={handleListingCreate}
                onUpdateListing={handleListingUpdate}
                onCreateStripeConnectLink={handleStripeConnectLink}
              />
            }
          />
          <Route
            path="/orders/:orderId"
            element={
              <OrderPage
                currentUser={currentUser}
                snapshot={snapshot}
                loading={loading}
                onLoadOrder={handleOrderLoad}
                onSendMessage={handleOrderMessage}
                onUploadFile={handleOrderFileUpload}
                onUpdateOrderStatus={handleOrderStatusUpdate}
                onSubmitReview={handleOrderReview}
              />
            }
          />
          <Route
            path="/messages"
            element={
              <MessagesPage
                currentUser={currentUser}
                snapshot={snapshot}
                loading={loading}
              />
            }
          />
          <Route
            path="/account"
            element={
              <AccountPage
                currentUser={currentUser}
                snapshot={snapshot}
                loading={loading}
                authLoading={authLoading}
                onLoadProfile={handleProfileLoad}
                onUpdateProfile={handleProfileUpdate}
                onDeleteListing={handleListingDelete}
                onCreateStripeConnectLink={handleStripeConnectLink}
              />
            }
          />
          <Route path="/list-your-agent" element={<Navigate replace to="/studio" />} />
          <Route path="/login" element={<LoginPage currentUser={currentUser} />} />
          <Route path="/auth" element={<Navigate replace to="/login" />} />
          <Route path="/how-agentichire-works" element={<HowItWorksPage />} />
          <Route path="/how-agenthire-works" element={<Navigate replace to="/how-agentichire-works" />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
        </Routes>
        <BuilderInviteModal currentUser={currentUser} />
      </MarketplaceLayout>
    </BrowserRouter>
  );
}

export default App;
